/**
 * Static customer testimonial entries for the marketing homepage.
 * Add a new voice by appending one object — layout scales automatically.
 *
 * Embeds always use the production host so local marketing previews still play
 * clips that only exist in prod (relative /embed/clips/... would 404 on local).
 *
 * Copy (video title, quote, role) lives in `<locale>/marketing.json` under
 * `kundenstimmenData`; names and company names are proper nouns and stay here.
 */

import type { Translate } from "@/lib/i18n/translate";

export interface Kundenstimme {
  readonly id: string;
  /** Convex clip id — used to build the public embed URL. */
  readonly clipId: string;
  readonly videoTitle: string;
  readonly quote: string;
  readonly name: string;
  readonly role?: string;
  readonly company?: string;
  /** Static 16:9 poster. Click replaces it with the embed. */
  readonly poster?: KundenstimmePoster;
}

export interface KundenstimmePoster {
  /** 1920×1080 JPEG under /public; next/image serves AVIF/WebP per viewport. */
  readonly src: string;
  /** Localized alt text (SEO: "Hausverwaltung KI", "KI für Hausverwaltungen"). */
  readonly alt: string;
  /** Tiny inline WebP, shown blurred until the poster has loaded. */
  readonly blurDataURL: string;
}

/** Public clip embed URL (prod). Marketing homepage always points here. */
export function kundenstimmeEmbedSrc(
  clipId: string,
  options?: { readonly autoplay?: boolean },
): string {
  const base = `https://hausverwalter.ai/embed/clips/${clipId}`;
  return options?.autoplay === true ? `${base}?autoplay=1` : base;
}

/** `useTranslations("marketing.kundenstimmenData")`. */
export function kundenstimmen(t: Translate): readonly Kundenstimme[] {
  return [
    {
      id: "sebastian-loellgen",
      clipId: "n98tvhws6ne7fxsr0ndhztv0z98c65nf",
      videoTitle: t("sebastianLoellgenVideoTitel"),
      quote: t("sebastianLoellgenZitat"),
      name: t("sebastianLoellgen"),
      company: "WLGV GmbH",
      role: t("sebastianLoellgenRolle"),
      poster: {
        src: "/marketing/kundenstimmen/ki-hausverwaltung-kundenstimme-sebastian-loellgen-wlgv.jpg",
        alt: t("sebastianLoellgenPosterAlt"),
        blurDataURL:
          "data:image/webp;base64,UklGRsQAAABXRUJQVlA4ILgAAACwBACdASoYAA4APu1iqU2ppaOiMAgBMB2JbACdMoAlvxICgXc1b5I2pWm4i8cAAP32pcPYk91QoRoff43kAdfr2s/i9m6xfyOTRTfqMnVkn4nGWAJ7MKhDIC1K8KYRv2p8GztRYKLmmXJntTtLy7tQSfznxBSkWrE5VfOOLgE+nj43Hl85eEiXe9rIoq1LxaoG3jWkQv4V6yGI35ZdPHQ//GBb/o1Z3s6LcBDnHkkgImH2jmUlwAAA",
      },
    },
    {
      id: "eduard-frank",
      clipId: "n98kjtyk9yh5pgygbbvdqxpetd8bx8df",
      videoTitle: t("eduardFrankVideoTitel"),
      quote: t("eduardFrankZitat"),
      name: t("eduardFrank"),
      company: "Borgmann-Frank Immobilien GmbH",
      role: t("eduardFrankRolle"),
      poster: {
        src: "/marketing/kundenstimmen/ki-fuer-hausverwaltungen-kundenstimme-eduard-frank-borgmann-frank.jpg",
        alt: t("eduardFrankPosterAlt"),
        blurDataURL:
          "data:image/webp;base64,UklGRqAAAABXRUJQVlA4IJQAAAAwBACdASoYAA4APu1iqU2ppaOiMAgBMB2JYgCdACPpeiSC7QrZhwW4T0AA+Vf10F8HJRK/ewl/Oy60873Q3Tr0yuiIHE6SzfZUkeBTd+zZZV5tbGvSEOysYTOe0vVMAsUHSa7hc0Vt+mjrdHZ385a1oNtUYHF3Kbq9yN2IfN1DWWqMj0sfUnQZVBejS4kd4YBUnwAA",
      },
    },
    {
      id: "alexander-richter",
      clipId: "n98y7b9c9jf0bev7swmqt7znh18ec3wa",
      videoTitle: t("alexanderRichterVideoTitel"),
      quote: t("alexanderRichterZitat"),
      name: t("alexanderRichter"),
      company: "Scharfenberger Hausverwaltung",
      role: t("alexanderRichterRolle"),
      poster: {
        src: "/marketing/kundenstimmen/hausverwaltung-ki-kundenstimme-alexander-richter-scharfenberger.jpg",
        alt: t("alexanderRichterPosterAlt"),
        blurDataURL:
          "data:image/webp;base64,UklGRoQAAABXRUJQVlA4IHgAAADwAwCdASoYAA4APu1iqU2ppaOiMAgBMB2JZQC7ABZMWpJStb665gaQAP6x2Fjlvw8GeVZFpwpBalx7QsCqF/ZflDKm0+jPv+P3ofKm9qEehXzk9If22lYBcONXpVcAj9+dAYzdX8qtFeDnMJEuWVBeoicVfOp24AA=",
      },
    },
  ];
}
